// Runtime application config. Deployed environments serve GET /config.json
// from the backend (same origin as the SPA); every field it carries wins over
// the build-time VITE_* value, so one built bundle works in any environment.
// When the fetch fails or a field is missing (Vite dev server, API-only
// deployments), the import.meta.env values stay in effect — local dev needs
// no changes.

export type AppConfig = {
  auth0Domain?: string
  auth0ClientId?: string
  auth0ScrClientId?: string
  auth0Audience?: string
  auth0Scope?: string
  sentryDsn?: string
  sentryRelease?: string
  sentryOrg?: string
  deployEnv?: string
  apiBaseUrl: string
}

const RUNTIME_KEYS = [
  "auth0Domain",
  "auth0ClientId",
  "auth0ScrClientId",
  "auth0Audience",
  "auth0Scope",
  "sentryDsn",
  "sentryRelease",
  "sentryOrg",
  "deployEnv",
  "apiBaseUrl",
] as const satisfies readonly (keyof AppConfig)[]

let runtimeConfig: Partial<AppConfig> = {}

// import.meta.env is read per call (not snapshotted at module load) so tests
// can stub individual VITE_* vars and consumers keep their lazy-read
// semantics.
export function getAppConfig(): AppConfig {
  return {
    auth0Domain:
      runtimeConfig.auth0Domain ??
      (import.meta.env.VITE_AUTH0_DOMAIN as string | undefined),
    auth0ClientId:
      runtimeConfig.auth0ClientId ??
      (import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined),
    auth0ScrClientId:
      runtimeConfig.auth0ScrClientId ??
      (import.meta.env.VITE_AUTH0_SCR_CLIENT_ID as string | undefined),
    auth0Audience:
      runtimeConfig.auth0Audience ??
      (import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined),
    auth0Scope:
      runtimeConfig.auth0Scope ??
      (import.meta.env.VITE_AUTH0_SCOPE as string | undefined),
    sentryDsn:
      runtimeConfig.sentryDsn ??
      (import.meta.env.VITE_SENTRY_DSN as string | undefined),
    sentryRelease:
      runtimeConfig.sentryRelease ??
      (import.meta.env.VITE_SENTRY_RELEASE as string | undefined),
    sentryOrg:
      runtimeConfig.sentryOrg ??
      (import.meta.env.VITE_SENTRY_ORG as string | undefined),
    deployEnv:
      runtimeConfig.deployEnv ??
      (import.meta.env.VITE_DEPLOY_ENV as string | undefined),
    // "" from /config.json is meaningful (same-origin relative URLs), so the
    // runtime value is tested for presence, not truthiness.
    apiBaseUrl:
      runtimeConfig.apiBaseUrl ??
      (import.meta.env.VITE_API_BASE_URL || "http://localhost:3000"),
  }
}

/**
 * Fetch /config.json and keep its fields as runtime overrides. Must complete
 * before the app modules load (see main.tsx) — the API client and Sentry read
 * their config at import time. Any failure leaves the build-time values in
 * effect and is deliberately silent: the Vite dev server has no /config.json.
 */
export async function loadAppConfig(): Promise<void> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" })
    if (!res.ok) return
    // A non-JSON response means some other server answered (e.g. an HTML
    // fallback) — treat it as "no runtime config".
    if (!(res.headers.get("content-type") ?? "").includes("application/json")) {
      return
    }
    const fetched: unknown = await res.json()
    if (typeof fetched !== "object" || fetched === null) return

    const overrides: Partial<AppConfig> = {}
    for (const key of RUNTIME_KEYS) {
      const value = (fetched as Record<string, unknown>)[key]
      if (typeof value === "string") overrides[key] = value
    }
    runtimeConfig = overrides
  } catch {
    // Network error — build-time config stays in effect.
  }
}
